import type { PerspectiveLensScore, PerspectiveReportDimension } from "~vue/appTypes";
import { type Dimension, PERSPECTIVE_LENS_CARD_DATA, PERSPECTIVE_TYPES, type PerspectiveLens, TYPE_COLORS } from "~vue/pages/perspectiveTypes";

const DIMENSION_CENTER_SCORE = 0;
const DIMENSION_MIN_SCORE = -3;
const DIMENSION_MAX_SCORE = 3;

/** Maps a binned score (−3 … +3) to a percentage (0 … 100) along the dimension track. */
export const scoreToPercent = (binnedScore: number): number => {
    const clamped = Math.min(Math.max(binnedScore, DIMENSION_MIN_SCORE), DIMENSION_MAX_SCORE);
    return ((clamped - DIMENSION_MIN_SCORE) / (DIMENSION_MAX_SCORE - DIMENSION_MIN_SCORE)) * 100;
};

export type DimensionSummary = {
    dimension: Dimension;
    summary: string;
    shortSummary: string;
    percent: number;
    leftLabel: string;
    rightLabel: string;
};

export const calculateDimensionSummary = (dimension: PerspectiveReportDimension): DimensionSummary => {
    const binnedScore = dimension.binned_score;
    const percent = scoreToPercent(binnedScore);
    const leftLabel = dimension.left_label;
    const rightLabel = dimension.right_label;
    let summary = `You tend to be balanced between ${leftLabel} and ${rightLabel}.`;
    let shortSummary = "Balanced";

    if (binnedScore < DIMENSION_CENTER_SCORE) {
        const magnitude = Math.abs(binnedScore);
        const intensity = magnitude >= 3 ? "strongly" : magnitude === 2 ? "more" : "slightly";
        summary = `You tend to be ${intensity} more ${leftLabel} than ${rightLabel}.`;
        const shortIntensity = magnitude >= 2 ? "Primarily" : "Slightly";
        shortSummary = `${shortIntensity} ${leftLabel}`;
    } else if (binnedScore > DIMENSION_CENTER_SCORE) {
        const magnitude = Math.abs(binnedScore);
        const intensity = magnitude >= 3 ? "strongly" : magnitude === 2 ? "more" : "slightly";
        summary = `You tend to be ${intensity} more ${rightLabel} than ${leftLabel}.`;
        const shortIntensity = magnitude >= 2 ? "Primarily" : "Slightly";
        shortSummary = `${shortIntensity} ${rightLabel}`;
    }

    return {
        dimension: dimension.dimension,
        summary,
        shortSummary,
        percent,
        leftLabel,
        rightLabel,
    };
};

export const calculateLensSummary = (lensScore: PerspectiveLensScore, leftLabel: string, rightLabel: string): string => {
    const binnedScore = lensScore.binned_score;
    const clampedScore = Math.min(Math.max(binnedScore, DIMENSION_MIN_SCORE), DIMENSION_MAX_SCORE);

    let summary = `You are balanced between ${leftLabel} and ${rightLabel}`;

    if (clampedScore < DIMENSION_CENTER_SCORE) {
        const magnitude = Math.abs(clampedScore);
        const intensity = magnitude >= 3 ? "strongly" : magnitude === 2 ? "more" : "slightly";
        summary = `You lean ${intensity} ${leftLabel}`;
    } else if (clampedScore > DIMENSION_CENTER_SCORE) {
        const magnitude = Math.abs(clampedScore);
        const intensity = magnitude >= 3 ? "strongly" : magnitude === 2 ? "more" : "slightly";
        summary = `You lean ${intensity} ${rightLabel}`;
    }
    return summary;
};

export const mapBinnedScoreToCardText = (lens: PerspectiveLens, binnedScore: number): string => {
    const cardData = PERSPECTIVE_LENS_CARD_DATA[lens];
    if (binnedScore <= -2) {
        return cardData.cardNegative;
    } else if (binnedScore === -1) {
        return cardData.cardSlightlyNegative;
    } else if (binnedScore === 0) {
        return cardData.cardNeutral;
    } else if (binnedScore === 1) {
        return cardData.cardSlightlyPositive;
    } else {
        return cardData.cardPositive;
    }
};

export const getPerspectiveColor = (letters: string | null | undefined): string | null => {
    if (!letters) {
        return null;
    }
    return TYPE_COLORS[letters] ?? null;
};

export const getPerspectiveLetters = (archetype: string | null | undefined): string | undefined =>
    Object.keys(PERSPECTIVE_TYPES).find((code) => PERSPECTIVE_TYPES[code].title === archetype);

export const getPerspectiveProfileTitle = (letters: string | null | undefined): string | null => {
    if (!letters) {
        return null;
    }
    const title = PERSPECTIVE_TYPES[letters]?.title;
    return title ? `${title} Profile` : null;
};
